import React, { useEffect, useState, useCallback } from "react";
import http from "../services/http";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { Music, ApiResponse } from "../types/luna";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../contexts/SnackbarContext";
import AdminLayout from "../components/AdminLayout";

interface MusicListResponse {
  musics: Music[];
}

const MusicList: React.FC = () => {
  const { logout, token } = useAuth();
  const navigate = useNavigate();
  const { notify } = useSnackbar();
  const [musics, setMusics] = useState<Music[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"category" | "artist">("category");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [artistId, setArtistId] = useState<number | null>(null);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [artists, setArtists] = useState<{ id: number; name: string }[]>([]);
  const [artistsLoading, setArtistsLoading] = useState(false);

  // 加载分类列表
  useEffect(() => {
    const loadCategories = async () => {
      if (!token) return;
      setCategoriesLoading(true);
      try {
        const res = await http.get(`category`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.code === "000000") {
          const list = (res.data.data?.categories ?? []).map((c: any) => ({
            id: c.id,
            name: c.name,
          }));
          setCategories(list);
          setCategoryId((prev) => {
            if (!prev && list.length > 0) {
              return list[0].id;
            }
            return prev;
          });
        } else {
          notify(res.data?.message || "分类加载失败", "error");
        }
      } catch (err: any) {
        console.error("加载分类失败:", err);
        if (err.response?.status === 401) {
          notify("登录已过期，请重新登录", "error");
          logout();
          navigate("/");
        } else {
          notify("网络错误，分类加载失败", "error");
        }
      } finally {
        setCategoriesLoading(false);
      }
    };
    loadCategories();
  }, [token, logout, navigate, notify]);

  // 加载艺术家列表
  useEffect(() => {
    const loadArtists = async () => {
      if (!token) return;
      setArtistsLoading(true);
      try {
        const res = await http.get(`artist`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.code === "000000") {
          const list = (res.data.data?.artists ?? []).map((a: any) => ({
            id: a.id,
            name: a.name,
          }));
          setArtists(list);
          setArtistId((prev) => {
            if (!prev && list.length > 0) {
              return list[0].id;
            }
            return prev;
          });
        } else {
          notify(res.data?.message || "艺术家加载失败", "error");
        }
      } catch (err: any) {
        console.error("加载艺术家失败:", err);
        if (err.response?.status === 401) {
          notify("登录已过期，请重新登录", "error");
          logout();
          navigate("/");
        } else {
          notify("网络错误，艺术家加载失败", "error");
        }
      } finally {
        setArtistsLoading(false);
      }
    };
    loadArtists();
  }, [token, logout, navigate, notify]);

  // 加载音乐数据
  const loadMusics = useCallback(async () => {
    if (!token) {
      notify("请先登录", "error");
      navigate("/");
      return;
    }

    if (mode === "category" && !categoryId) {
      return;
    }
    if (mode === "artist" && !artistId) {
      return;
    }
    setLoading(true);
    try {
      const url =
        mode === "category"
          ? `music/category?categoryId=${categoryId}`
          : `music/artist?artistId=${artistId}`;

      const res = await http.get<ApiResponse<MusicListResponse>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.data.code === "000000") {
        setMusics(res.data.data.musics);
      } else {
        notify(res.data.message || "加载失败", "error");
      }
    } catch (err: any) {
      console.error(err);
      if (err.response?.status === 401) {
        notify("登录已过期，请重新登录", "error");
        logout();
        navigate("/");
      } else {
        notify("网络错误", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [mode, categoryId, artistId, token, navigate, logout, notify]);

  useEffect(() => {
    loadMusics();
  }, [mode, categoryId, artistId, loadMusics]);

  return (
    <AdminLayout title="音乐">
      <Box display="flex" gap={2} mb={3}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>模式</InputLabel>
          <Select
            value={mode}
            label="模式"
            onChange={(e) => setMode(e.target.value as "category" | "artist")}
          >
            <MenuItem value="category">按分类</MenuItem>
            <MenuItem value="artist">按艺术家</MenuItem>
          </Select>
        </FormControl>

        {mode === "category" ? (
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>分类</InputLabel>
            <Select
              value={categoryId ?? ""}
              label="分类"
              disabled={categoriesLoading || categories.length === 0}
              onChange={(e) => setCategoryId(Number(e.target.value))}
            >
              {categoriesLoading && (
                <MenuItem value="" disabled>
                  加载中...
                </MenuItem>
              )}
              {!categoriesLoading &&
                categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        ) : (
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>艺术家</InputLabel>
            <Select
              value={artistId ?? ""}
              label="艺术家"
              disabled={artistsLoading || artists.length === 0}
              onChange={(e) => setArtistId(Number(e.target.value))}
            >
              {artistsLoading && (
                <MenuItem value="" disabled>
                  加载中...
                </MenuItem>
              )}
              {!artistsLoading &&
                artists.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {loading ? (
        <Box textAlign="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : musics.length === 0 ? (
        <Box textAlign="center" mt={5}>
          <Typography variant="h6" color="text.secondary">
            暂无音乐，试试切换分类或艺术家
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {musics.map((music) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={music.id}>
              <Card>
                <CardMedia
                  component="img"
                  height="180"
                  image={music.coverUrl}
                  alt={music.title}
                />
                <CardContent>
                  <Typography variant="h6">{music.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {music.artist?.name} / {music.category?.name}
                  </Typography>
                  {music.releaseDate && (
                    <Typography variant="body2" color="text.secondary">
                      发行日：{music.releaseDate.slice(0, 10)}
                    </Typography>
                  )}
                  <audio
                    controls
                    src={music.audioUrl}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </AdminLayout>
  );
};

export default MusicList;
